/**
 * PATH: apps/hr/src/pages/employees/employee-detail/EmployeeSidebar.tsx
 * PURPOSE: Right sidebar with leave balances, pay rate, and quick stats
 */

import { CalendarOff, DollarSign, Shield } from 'lucide-react'
import { formatCurrency, formatStatus } from '../../../lib/utils'
import type { Employee } from '../../../types'
import { InfoCard } from './detail-shared-ui'

export default function EmployeeSidebar({ employee }: { employee: Employee }) {
  return (
    <div className="space-y-4">
      {/* Leave Balances */}
      {employee.leaveBalances && (
        <InfoCard title="Leave Balances" icon={CalendarOff}>
          <dl className="space-y-2">
            {[
              { label: 'Annual', value: employee.leaveBalances.annual },
              { label: 'Sick', value: employee.leaveBalances.sick },
              { label: 'Personal', value: employee.leaveBalances.personal },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between">
                <dt className="text-sm text-slate-500 dark:text-slate-400">{label}</dt>
                <dd className="text-sm font-medium text-slate-900 dark:text-white">{value} days</dd>
              </div>
            ))}
            {employee.leaveBalances.longService > 0 && (
              <div className="flex justify-between">
                <dt className="text-sm text-slate-500 dark:text-slate-400">Long Service</dt>
                <dd className="text-sm font-medium text-slate-900 dark:text-white">{employee.leaveBalances.longService} days</dd>
              </div>
            )}
          </dl>
        </InfoCard>
      )}

      {/* Pay Rate */}
      {employee.payRate && (
        <InfoCard title="Pay Rate" icon={DollarSign}>
          <dl className="space-y-2">
            <div className="flex justify-between">
              <dt className="text-sm text-slate-500 dark:text-slate-400">Hourly Rate</dt>
              <dd className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(employee.payRate.hourlyRate)}/hr</dd>
            </div>
            {employee.payRate.annualSalary && (
              <div className="flex justify-between">
                <dt className="text-sm text-slate-500 dark:text-slate-400">Annual Salary</dt>
                <dd className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(employee.payRate.annualSalary)}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-sm text-slate-500 dark:text-slate-400">Super Rate</dt>
              <dd className="text-sm font-medium text-slate-900 dark:text-white">{employee.payRate.superRate > 0 ? `${employee.payRate.superRate}%` : '—'}</dd>
            </div>
          </dl>
        </InfoCard>
      )}

      {/* Quick Stats */}
      <InfoCard title="Quick Stats" icon={Shield}>
        <dl className="space-y-2">
          <div className="flex justify-between">
            <dt className="text-sm text-slate-500 dark:text-slate-400">Status</dt>
            <dd className="text-sm font-medium text-slate-900 dark:text-white capitalize">{formatStatus(employee.status)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-slate-500 dark:text-slate-400">Tenure</dt>
            <dd className="text-sm font-medium text-slate-900 dark:text-white">
              {employee.hireDate ? `${Math.floor((new Date().getTime() - new Date(employee.hireDate).getTime()) / (1000 * 60 * 60 * 24 * 365))} years` : '—'}
            </dd>
          </div>
        </dl>
      </InfoCard>
    </div>
  )
}
